import type { DragEvent as ReactDragEvent, MouseEvent as ReactMouseEvent } from "react";
import type { MultiComment } from "../zekay/comments";

export type ExcursionStatus = "scheduled" | "validated" | "cancelled";

export const ExcursionStatusPending = "scheduled" as const;
export const ExcursionStatusDone = "validated" as const;
export const ExcursionStatusCancelled = "cancelled" as const;

export type ExcursionCustomer = {
  id: number;
  color?: string;
  emoji?: string;
  comments?: MultiComment[];
};

export type ExcursionUser = {
  id: number;
  color?: string;
  comments?: MultiComment[];
};

export type BookingStatus = "pending" | "validated" | "rejected" | "cancelled";

/** List/get payload; when `planning_anonymized`, API JSON keeps user_id but omits reason fields. */
export type ExcursionBooking = {
  id: number;
  company_id?: number;
  company_name?: string;
  excursion_id?: number;
  user_id: number;
  user_name?: string;
  user_is_employee?: boolean;
  status?: BookingStatus;
  created_at?: string;
  updated_at?: string;
  planning_slot_label?: string;
  planning_anonymized?: boolean;
  excursion_template?: string;
  group_template?: string;
  date?: string;
  start_time?: string | null;
  end_time?: string | null;
};

export type ExcursionBookingFilters = {
  status?: BookingStatus;
  excursion_id?: number;
  startTime?: string;
  endTime?: string;
  from?: string;
  to?: string;
  scope?: "tenant";
};

export type CreateExcursionBookingRequest = {
  excursion_id: number;
  user_id: number;
};

export type UpdateExcursionBookingRequest = {
  status: BookingStatus;
};

export type ExcursionGroup = {
  id: number;
  excursion_id: number;
  position: number;
  excursion_customers?: ExcursionCustomer[];
  excursion_users?: ExcursionUser[];
  color?: string;
  comments?: MultiComment[];
  params?: Record<string, unknown>;
  excursion_group_param_id?: number;
  created_at?: string;
};

export type Excursion = {
  id: number;
  company_id: number;
  date: string;
  start_time?: string | null;
  end_time?: string | null;
  name: string;
  color?: string;
  status?: ExcursionStatus;
  /** Set when status is cancelled (optional). */
  cancellation_reason?: string | null;
  created_at?: string;
  params?: Record<string, unknown>;
  groups: ExcursionGroup[];
  params_template_id?: number;
  location?: string;
  description?: string;
};

export type ExcursionParams = {
  id?: number;
  company_id?: number;
  name?: string;
  description?: string;
  color?: string;
  params_template?: Record<string, unknown>;
  requires_user_validation?: boolean;
  max_people_per_excursion?: number;
  max_groups_per_excursion?: number;
  should_be_used_by_default?: boolean;
  created_at?: string;
  updated_at?: string;
};

export type ExcursionParamsInput = {
  name?: string;
  description?: string;
  color?: string;
  params_template?: Record<string, unknown>;
  requires_user_validation?: boolean;
  max_people_per_excursion?: number;
  max_groups_per_excursion?: number;
  should_be_used_by_default?: boolean;
};

export type ExcursionGroupParams = {
  id?: number;
  company_id?: number;
  name?: string;
  description?: string;
  color?: string;
  params_template?: Record<string, unknown>;
  requires_user_validation?: boolean;
  max_users_per_group?: number;
  max_customers_per_group?: number;
  should_be_used_by_default?: boolean;
  created_at?: string;
  updated_at?: string;
};

export type ExcursionGroupParamsInput = {
  name?: string;
  description?: string;
  color?: string;
  params_template?: Record<string, unknown>;
  requires_user_validation?: boolean;
  max_users_per_group?: number;
  max_customers_per_group?: number;
  should_be_used_by_default?: boolean;
};

export type ExcursionGroupRequest = {
  id?: number;
  excursion_group_param_id?: number;
  excursion_customers?: ExcursionCustomer[];
  excursion_users?: ExcursionUser[];
  params?: Record<string, unknown>;
  color?: string;
  comments?: MultiComment[];
};

export type ExcursionRequest = {
  date: string;
  start_time?: string | null;
  end_time?: string | null;
  name: string;
  color?: string;
  status?: ExcursionStatus | null;
  /** Stored when excursion is cancelled; cleared when status is not cancelled. */
  cancellation_reason?: string | null;
  location?: string | null;
  description?: string | null;
  params?: Record<string, unknown> | null;
  params_template?: ExcursionParams | null;
  groups: ExcursionGroupRequest[];
};

export type GroupLimits = {
  max_users_per_group?: number;
  max_customers_per_group?: number;
};

export type ExcursionTemplateLimitsItem = {
  id?: number;
  max_people_per_excursion?: number;
  max_groups_per_excursion?: number;
  /** Key → type (filtered), for tooltip when excursion.params_template has only `id`. */
  additional_param_fields?: Record<string, string>;
};

export type GroupCustomerLabel = {
  id: number;
  first_name?: string;
  last_name?: string;
  qualification?: string;
  name?: string;
  emoji?: string;
};

export type GroupUserLabel = {
  id: number;
  first_name?: string;
  last_name?: string;
  qualification?: string;
  name?: string;
};

export type GroupLabels = {
  user_name?: string;
  user_first_name?: string;
  user_last_name?: string;
  user_qualification?: string;
  customer_name?: string;
  customer_last_name?: string;
  customer_first_name?: string;
  customer_qualification?: string;
  customers?: GroupCustomerLabel[];
  /** Labels for excursion_users (guides/instructors). */
  guides?: GroupUserLabel[];
};

/** Planning DnD: spinner overlay on the drop target while the API update runs. */
export type ExcursionChipDropPending =
  | { excursionId: number; surface: "group"; groupIndex: number }
  | { excursionId: number; surface: "card" };

export type ExcursionPlanningCardContext = {
  canEdit: boolean;
  /** When true, user can open the sortie drawer (e.g. view with CustomersSync; mutations still need `canEdit`). */
  canOpenExcursionDrawer: boolean;
  getGroupLabels: (ex: Excursion) => GroupLabels[];
  makeGroupChipDropHandlers: (targetExcursionId: number, targetGroupIndex: number) => object;
  makeExcursionCardDragHandlers: (ex: Excursion) => object;
  makeExcursionCardDropHandlers: (targetExcursionId: number) => object;
  openPlanningGroupsDrawer: (excursionId: number) => void;
  /** Double-click on a planning card: open full ExcursionModal for edit (requires `canEdit`). */
  onOpenExcursionFullModal?: (e: ReactMouseEvent, excursion: Excursion) => void;
  tooltipForceClose?: boolean;
  clientChipDragInProgress?: boolean;
  clientChipDragSourceExcursionId: number | null;
  onClientChipDragStart?: (sourceExcursionId: number) => void;
  onClientChipDragEnd?: () => void;
  onDragEnterCard: (excursionId: number) => void;
  onDragLeaveCard: (excursionId: number, e: ReactDragEvent<Element>) => void;
  /** Excursion card Popper `forceOpen` target while dragging PersonChip over the grid. */
  planningChipDragTooltipTargetId: number | null;
  /** Clears chip-drag forced `ExcursionCardTooltip`; call when mouse leaves Popper after drop. */
  dismissPlanningForcedPopoverTarget?: () => void;
  excursionTemplatesList?: ExcursionTemplateLimitsItem[];
  bookingStatusMap?: Record<number, Record<number, BookingStatus>>;
  getCustomerInProgressTrainingsTooltip?: (customerId: number) => string | null;
  chipDropPending: ExcursionChipDropPending | null;
};
